import { translateBackend } from '@assets/i18n/config';
import type { ForeignTaxInformation } from '@interfaces/foreignTaxInformation';
import type { TranslatedString } from '@interfaces/shared';
import type { SxProps } from '@mui/material';
import { Autocomplete } from '@ovComponents/2-component/autocomplete/autocomplete';
import { HelpText } from '@ovComponents/2-component/helpText/helpText';
import { useHelpTextTokens } from '@ovComponents/2-component/helpText/helpText.tokens';

import InfoDialog from '@ovComponents/2-component/infoDialog/infoDialog';
import { Radio, RadioGroup } from '@ovComponents/2-component/radio/radio';
import { useTextFieldTokens } from '@ovComponents/2-component/textField/textField.tokens';
import { useCountryList } from '@ovComponents/resources';
import { Box, Grid, IconButton, Typography, useTheme } from 'hephaestus/web';
import {
  AddRounded as AddRoundedIcon,
  DeleteRounded as DeleteRoundedIcon,
} from 'hephaestus/webIcons';
import { useTranslation } from 'react-i18next';
import { MaskedTaxIdField } from '../maskedTaxIdField/maskedTaxIdField';

type CustomLabelProps = TranslatedString & { enabled: boolean };

export const ForeignTaxField = ({
  readonly = false,
  disabled = false,
  foreignTax,
  setForeignTax,
  error,
  label,
  sx,
  infoTooltip,
  customLabels,
  lockMessage,
  locked,
}: {
  setForeignTax: (value: ForeignTaxInformation[]) => void;
  readonly?: boolean;
  disabled?: boolean;
  foreignTax: ForeignTaxInformation[];
  error?: boolean;
  label: string;
  sx?: SxProps;
  infoTooltip?: string;
  customLabels?: { mainSwitch: CustomLabelProps };
  lockMessage?: string;
  locked?: boolean;
}) => {
  const { t } = useTranslation('components');
  const countryList = useCountryList();
  const theme = useTheme();
  const tokens = useTextFieldTokens(theme);
  const helpTextTokens = useHelpTextTokens(theme);

  const shouldDisplayInDialog = helpTextTokens.displayInDialog;

  const addNewForeignTax = (): void => {
    setForeignTax([
      ...foreignTax,
      { foreignTaxCountry: '', foreignTaxNumber: '' },
    ]);
  };
  const resetForeignTax = (): void => {
    setForeignTax([]);
  };
  const removeFromForeignTax = (index: number) => {
    setForeignTax([...foreignTax.filter((_element, idx) => idx !== index)]);
  };
  const updateTaxItemInList = (
    value: string,
    index: number,
    property?: 'country' | 'number',
  ) =>
    foreignTax?.map((taxItem, mapIndex) => {
      if (index === mapIndex) {
        return property === 'number'
          ? { ...taxItem, foreignTaxCountry: value }
          : { ...taxItem, foreignTaxNumber: value };
      }
      return taxItem;
    }) ?? [];
  const onAutoCompleteChanged = (index: number, value: string) => {
    if (value) {
      const countryCode =
        countryList.find((country) => country.name === value)?.code ?? '';
      setForeignTax([...updateTaxItemInList(countryCode, index, 'number')]);
    }
  };

  return (
    <Box sx={sx as any}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
        }}
      >
        <Typography
          variant='labelSmall'
          sx={
            {
              mb: '4px',
              mr: '4px',
              color: error ? tokens.errorColor : tokens.labelColor,
            } as any
          }
        >
          {label}
          {infoTooltip && shouldDisplayInDialog && (
            <InfoDialog information={infoTooltip} />
          )}
        </Typography>
      </Box>
      {infoTooltip && !shouldDisplayInDialog && (
        <HelpText text={infoTooltip} tone='neutral' />
      )}
      <RadioGroup
        value={foreignTax?.length > 0 ? 'yes' : 'no'}
        error={error}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          if (event.target.value === 'yes') {
            addNewForeignTax();
          } else {
            resetForeignTax();
          }
        }}
        disabled={disabled || readonly || locked}
        label={
          customLabels?.mainSwitch?.enabled === true
            ? translateBackend(customLabels?.mainSwitch)
            : t('components:taxInformationField.label')
        }
        testId='foreign-tax-switch'
      >
        <Radio
          disabled={disabled || readonly || locked}
          testId={'foreign-tax-switch-option-no'}
          value={'no'}
          label={t('shared:no')}
        />

        <Radio
          disabled={disabled || readonly || locked}
          testId={'foreign-tax-switch-option-yes'}
          value={'yes'}
          label={t('shared:yes')}
        />
      </RadioGroup>

      {foreignTax?.length > 0
        ? foreignTax.map((item, index) => (
            <Grid
              key={index}
              container
              spacing={2}
              sx={{
                mt: 2,
                alignItems: 'flex-end',
              }}
            >
              <Grid key={`country-${index}`} size={{ md: 6, xs: 12 }}>
                <Autocomplete
                  testId={`country-autocomplete-${index}`}
                  value={
                    !item.foreignTaxCountry
                      ? null
                      : {
                          label:
                            countryList.find(
                              (country) =>
                                country.code ===
                                foreignTax[index].foreignTaxCountry,
                            )?.name ?? '',
                        }
                  }
                  isOptionEqualToValue={(option: any, value: any) =>
                    option.label === value.label
                  }
                  disabled={disabled || readonly}
                  onChange={(_e: any, value: any) => {
                    onAutoCompleteChanged(index, value?.label ?? '');
                  }}
                  error={error}
                  lockMessage={lockMessage}
                  locked={locked}
                  fullWidth
                  options={countryList.map((country) => ({
                    label: country.name,
                  }))}
                  label={t('components:taxInformationField.country')}
                />
              </Grid>
              <Grid
                key={`tin-${index}`}
                size={{ md: readonly ? 6 : 4, xs: readonly ? 12 : 8 }}
              >
                <MaskedTaxIdField
                  fullWidth
                  data-testid={`tin-${index}`}
                  value={foreignTax[index].foreignTaxNumber}
                  disclosedValue={foreignTax[index].foreignTaxNumberDisclosed}
                  label={t('components:taxInformationField.tin')}
                  error={error}
                  lockMessage={lockMessage}
                  locked={locked}
                  setValue={(newForeignTIN: any) => {
                    setForeignTax([
                      ...updateTaxItemInList(newForeignTIN, index),
                    ]);
                  }}
                  disabled={disabled || readonly}
                />
              </Grid>
              {readonly ? undefined : (
                <>
                  <Grid
                    key={`delete-icon-${index}`}
                    size={{ md: 1, xs: 2 }}
                    sx={{
                      display: 'flex',
                      alignItems: 'end',
                    }}
                  >
                    <IconButton aria-label='delete' disabled={disabled}>
                      <DeleteRoundedIcon
                        data-testid={`remove-button-${index}`}
                        sx={{ cursor: 'pointer' }}
                        onClick={() => removeFromForeignTax(index)}
                        fontSize='inherit'
                      />
                    </IconButton>
                    <IconButton aria-label='add' disabled={disabled}>
                      {index === foreignTax.length - 1 ? (
                        <AddRoundedIcon
                          data-testid='add-button'
                          sx={{ cursor: 'pointer' }}
                          onClick={() => addNewForeignTax()}
                          fontSize='inherit'
                        />
                      ) : undefined}
                    </IconButton>
                  </Grid>
                </>
              )}
            </Grid>
          ))
        : undefined}
    </Box>
  );
};

export default ForeignTaxField;
